import { ManifestJSONSchema } from "src/api/connectApi/manifestTypes";

// Reserved environment input-key prefix, mirroring the backend convention:
// an input named `tktl_backup__<key>` holds the backup value for the input
// named `<key>`.
export const BACKUP_INPUT_KEY_PREFIX = "tktl_backup__";

export const isBackupInputKey = (key: string): boolean =>
  key.startsWith(BACKUP_INPUT_KEY_PREFIX);

export const baseInputKey = (key: string): string =>
  isBackupInputKey(key) ? key.slice(BACKUP_INPUT_KEY_PREFIX.length) : key;

/**
 * A schema supports backup credentials only when its backup keys are an
 * exact mirror of the primary keys — the same rule as the backend's
 * `manifest_supports_backup_credential_set`.
 */
export const schemaSupportsBackupCredentials = (
  schema: ManifestJSONSchema | undefined,
): boolean => {
  const keys = Object.keys(schema?.properties || {});
  const backupKeys = keys.filter(isBackupInputKey);
  const primaryKeys = keys.filter((key) => !isBackupInputKey(key));
  return (
    backupKeys.length > 0 &&
    backupKeys.length === primaryKeys.length &&
    backupKeys.every((key) => primaryKeys.includes(baseInputKey(key)))
  );
};

export const primaryCredentialsSchema = (
  schema: ManifestJSONSchema,
): ManifestJSONSchema => ({
  ...schema,
  properties: Object.fromEntries(
    Object.entries(schema.properties || {}).filter(
      ([key]) => !isBackupInputKey(key),
    ),
  ),
  required: schema.required?.filter((key) => !isBackupInputKey(key)),
  secrets: schema.secrets?.filter((key) => !isBackupInputKey(key)),
});

/**
 * Backup fields use their own manifest definitions as-is — the manifest
 * author decides what matches the primary fields and what differs.
 */
export const backupCredentialsSchema = (
  schema: ManifestJSONSchema,
): ManifestJSONSchema => {
  const properties = schema.properties || {};
  const backupKeys = Object.keys(properties).filter(isBackupInputKey);
  return {
    ...schema,
    properties: Object.fromEntries(
      backupKeys.map((key) => [key, properties[key]]),
    ),
    // The manifest expresses backup requiredness via `dependentRequired`
    // (all-or-none), which the form does not parse; instead, a backup
    // field is required exactly when its primary counterpart is.
    required: backupKeys.filter((key) =>
      schema.required?.includes(baseInputKey(key)),
    ),
    secrets: schema.secrets?.filter(isBackupInputKey),
  };
};

export const hasBackupValues = (
  values: Record<string, unknown> | undefined,
): boolean =>
  Object.entries(values || {}).some(
    ([key, value]) => isBackupInputKey(key) && Boolean(value),
  );
